import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';

import { TracksByCategory } from './types';
import { MUSIC_LIBRARY_VARIABLES } from './constants';

import MobileSelect, { IOption, MOBILE_SELECT_VARIABLES } from '../MobileSelect';

import { ArrayElement, Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TMusicLibrary = ArrayElement<
  Normalize<Queries.MusicLibraryDataFragment['data']['content']>
>;

interface ICategories {
  tracksByCategory: TracksByCategory[];
  activeIndex: number;
  composeCategoryClickHandle: (index: number) => () => void;
  selectProps: {
    options: IOption[];
    onChange: (option: IOption) => void;
    value: IOption;
  };
}

const TabsContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    row-gap: 0.25rem;
    align-content: flex-start;
    justify-items: flex-start;

    ${mq.md} {
      display: none;
    }
  `}
`;

const Category = styled('span', filterProps('isActive'))<{ isActive: boolean }>`
  ${({ theme: { tp }, isActive }) => css`
    border-radius: 1rem;
    background-color: ${isActive ? `var(${MUSIC_LIBRARY_VARIABLES.accentColor}) ` : 'transparent'};
    cursor: pointer;
    padding: 0.75rem 1.5rem;
    line-height: 1.2;
    letter-spacing: -0.04em;
    white-space: nowrap;
    color: var(
      ${isActive
        ? MUSIC_LIBRARY_VARIABLES.categoryHighlightTextColor
        : MUSIC_LIBRARY_VARIABLES.categoryTextColor}
    );
    font-size: 2rem;

    ${tp.useFont(fonts => fonts.Switzer.light)};
  `}
`;

const CategoriesSelect = styled(MobileSelect)`
  ${({ theme: { mq } }) => css`
    display: none;

    ${mq.md} {
      ${MOBILE_SELECT_VARIABLES.backgroundColor}: var(${MUSIC_LIBRARY_VARIABLES.selectBackgroundColor});
      ${MOBILE_SELECT_VARIABLES.textColor}: var(${MUSIC_LIBRARY_VARIABLES.selectTextColor});
      ${MOBILE_SELECT_VARIABLES.activeTextColor}: var(${MUSIC_LIBRARY_VARIABLES.selectActiveTextColor});

      display: block;
      z-index: 3;
    }
  `}
`;

const Categories: React.FC<ICategories> = ({
  tracksByCategory,
  activeIndex,
  composeCategoryClickHandle,
  selectProps,
}) => {
  return tracksByCategory.length > 1 ? (
    <>
      <TabsContainer>
        {tracksByCategory.map(({ name }, index) => (
          <Category
            key={`${name}${index}`}
            isActive={index === activeIndex}
            onClick={composeCategoryClickHandle(index)}
          >
            {name}
          </Category>
        ))}
      </TabsContainer>
      <CategoriesSelect {...selectProps} />
    </>
  ) : null;
};

export const contentMusicLibraryCategoriesFragment = graphql`
  fragment MusicLibraryCategoriesData on MusicLibraryEnabledPage {
    ... on PrismicSolution {
      data {
        content {
          ... on PrismicSolutionDataContentMusicLibrary {
            items {
              music_library_category_name
            }
          }
        }
      }
    }

    ... on PrismicIndustry {
      data {
        content {
          ... on PrismicIndustryDataContentMusicLibrary {
            items {
              music_library_category_name
            }
          }
        }
      }
    }
  }
`;

export default Categories;
